import { constant, isArray, isFunction } from 'underscore';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { ErrorMessages } from '@bingads-webui-react/validation';
import { ValidatedControlAdapter } from './adapters/validated-control-adapter';
import { FormErrorContext } from './form-error-context';
import { processErrorMessages, processErrors } from './process-errors';

export const withFormError = (WrappedComponent, {
  filter: staticFilter = constant(false),
  mapper: staticMapper,
  Adapter = ValidatedControlAdapter,
  showError = true,
} = {}) => {
  const AdaptedWrappedComponent = Adapter(WrappedComponent);

  const FormErrorHoC = ({ formErrorProps, ...restProps }) => {
    const { filter = staticFilter, mapper = staticMapper, onUnhandledErrorsChange } = formErrorProps;

    const parentContext = useContext(FormErrorContext);
    const isIsolated = !parentContext;

    const {
      errors,
      errorMapper,
      addNodeFilter,
      removeNodeFilter,
      addNodeUnhandledErrors,
      removeNodeUnhandledErrors,
    } = isIsolated ? {} : parentContext;

    // Change tracking through the unified onChange callback on formErrorProps
    const [hasChanged, setHasChanged] = useState(false);
    const onChange = useCallback(() => {
      setHasChanged(true);
    }, []);

    // Reset change tracking upon receiving new errors collection
    useEffect(() => {
      setHasChanged(false);
    }, [errors]);

    // Unhandled/still displayed errors
    const filteredErrors = useMemo(
      () => (hasChanged || !isArray(errors) ? [] : processErrors(errors, filter)),
      [hasChanged, errors, filter]
    );

    const errorMessages = useMemo(
      () => processErrorMessages(filteredErrors, mapper || errorMapper),
      [filteredErrors, mapper, errorMapper]
    );

    // When props.filter changes re-register with the parent
    // to keep it up to date on all nodes' filters so it can track
    // what errors didn't get mapped to any of the nodes
    useEffect(() => {
      const node = { filter }; // boxing it for by-ref identification
      if (!isIsolated) {
        addNodeFilter(node);
      }
      return () => {
        if (!isIsolated) {
          removeNodeFilter(node);
        }
      };
    }, [isIsolated, filter, addNodeFilter, removeNodeFilter]);

    // When currently displayed/unhandled errors change re-register
    // with the parent to keep it up to date on all node's unhandled errors.
    useEffect(() => {
      const node = { filteredErrors }; // boxing it for by-ref identification
      if (!isIsolated) {
        addNodeUnhandledErrors(node);
      }
      return () => {
        if (!isIsolated) {
          removeNodeUnhandledErrors(node);
        }
      };
    }, [isIsolated, filteredErrors, addNodeUnhandledErrors, removeNodeUnhandledErrors]);

    useEffect(() => {
      if (isFunction(onUnhandledErrorsChange)) {
        onUnhandledErrorsChange(filteredErrors);
      }
    }, [onUnhandledErrorsChange, filteredErrors]);

    // Unified interface between withFormError and Form Error adapters to report changes of Wrapped controls
    const serverErrorAdapterProps = useMemo(() => ({
      onChange,
    }), [onChange]);

    return (
      <>
        <AdaptedWrappedComponent serverErrorAdapterProps={serverErrorAdapterProps} {...restProps} />
        { showError && <ErrorMessages messages={errorMessages} />}
      </>
    );
  };

  FormErrorHoC.propTypes = {
    formErrorProps: PropTypes.shape({
      filter: PropTypes.func,
      mapper: PropTypes.func,
      onUnhandledErrorsChange: PropTypes.func,
    }),
    validationProps: PropTypes.shape({
      alwaysShowError: PropTypes.bool,
    }),
  };

  FormErrorHoC.defaultProps = {
    formErrorProps: {},
    validationProps: {},
  };

  hoistNonReactStatics(FormErrorHoC, WrappedComponent);
  FormErrorHoC.displayName = `withFormError(${getDisplayName(WrappedComponent)})`;

  return FormErrorHoC;
};

export const withAlwaysShowError = (WrappedComponent) => {
  const HOC = ({ validationProps: validationPropsFromProps, ...restProps }) => {
    const { alwaysShowError: alwaysShowErrorFromProps } = validationPropsFromProps;

    const parentContext = useContext(FormErrorContext);
    const isIsolated = !parentContext;

    const {
      showAllValidationErrors,
    } = isIsolated ? {} : parentContext;
    const validationProps = useMemo(() => ({
      alwaysShowError: alwaysShowErrorFromProps || showAllValidationErrors,
      ...validationPropsFromProps,
    }), [alwaysShowErrorFromProps, showAllValidationErrors, validationPropsFromProps]);

    return <WrappedComponent validationProps={validationProps} {...restProps} />;
  };

  HOC.propTypes = {
    validationProps: PropTypes.shape({
      alwaysShowError: PropTypes.bool,
    }),
  };

  HOC.defaultProps = {
    validationProps: {},
  };

  hoistNonReactStatics(HOC, WrappedComponent);
  HOC.displayName = `withAlwaysShowError(${getDisplayName(WrappedComponent)})`;

  return HOC;
};
